import { useCallback, useEffect, useRef, useState } from 'react';
import { api, errMsg } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Panel, Tag } from '@/components/Common';
import { Button } from '@/components/ui/button';

const numeric = value => typeof value === 'number' && Number.isFinite(value);
const symbols = ['EURUSD','GBPUSD','USDJPY','AUDUSD','USDCHF','USDCAD','XAUUSD'];
const money = (value, currency) => numeric(value) ? `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${currency || ''}` : '—';
export function observerProjection(data) {
  if (!data || data.read_only !== true || data.execution_allowed !== false || data.trade_ready !== false
      || typeof data.paired !== 'boolean' || typeof data.connected !== 'boolean'
      || (data.connected && data.source !== 'cloud_mt5_real_observer')) throw new Error('REAL_OBSERVER_RESPONSE_INVALID');
  const fresh = data.paired && data.connected && numeric(data.receipt_age_seconds)
    && data.receipt_age_seconds >= 0 && data.receipt_age_seconds <= 5;
  const account = {};
  for (const key of ['balance','equity','profit','margin','margin_free']) account[key] = numeric(data.account?.[key]) ? data.account[key] : null;
  account.currency = /^[A-Z]{3}$/.test(data.account?.currency || '') ? data.account.currency : '';
  const quotes = {};
  for (const symbol of symbols) {
    const quote = data.quotes?.[symbol];
    if (numeric(quote?.bid) && numeric(quote?.ask) && quote.bid > 0 && quote.ask >= quote.bid) quotes[symbol] = {bid:quote.bid, ask:quote.ask};
  }
  return {paired:data.paired, connected:!!fresh, receipt_age_seconds:data.receipt_age_seconds,
    received_at:typeof data.received_at === 'string' ? data.received_at.slice(0,64) : null,
    login_masked:typeof data.binding?.login_masked === 'string' && /^\*+\d{1,4}$/.test(data.binding.login_masked) ? data.binding.login_masked : '—',
    server:data.binding?.server === 'AdmiralsGroup-Live' ? data.binding.server : '—',
    account:fresh ? account : null, quotes:fresh ? quotes : {}};
}

function OwnerObserverStatus({initialLive = true, revision = 0, paused = false}) {
  const [status,setStatus] = useState(null), [error,setError] = useState(''), [busy,setBusy] = useState(false);
  const [live,setLive] = useState(initialLive);
  const active = useRef(false), flight = useRef(null), expiry = useRef(null), epoch = useRef(0);
  const load = useCallback(async () => {
    if (!active.current || flight.current || paused) return;
    const controller = new AbortController(), version = epoch.current, started = Date.now();
    flight.current = controller; setBusy(true);
    try {
      const {data} = await api.get('/local-mt5/real-observer/status', {signal:controller.signal, timeout:10000});
      if (!active.current || controller.signal.aborted || version !== epoch.current) return;
      const projected = observerProjection(data);
      clearTimeout(expiry.current);
      const remaining = (5 - projected.receipt_age_seconds) * 1000 - Math.max(0,Date.now()-started);
      if (projected.connected && remaining <= 0) Object.assign(projected,{connected:false,account:null,quotes:{}});
      setStatus(projected); setError('');
      if (projected.connected) expiry.current = setTimeout(() => {
        if (active.current) setStatus(previous => previous ? {...previous,connected:false,account:null,quotes:{}} : null);
      },remaining);
    } catch (e) {
      if (active.current && !controller.signal.aborted && version === epoch.current) {
        clearTimeout(expiry.current); setStatus(null); setError(errMsg(e));
      }
    } finally {
      if (flight.current === controller) {
        flight.current = null;
        if (active.current) setBusy(false);
      }
    }
  },[paused]);
  useEffect(() => {
    active.current = true;
    return () => {active.current=false; epoch.current++; flight.current?.abort(); clearTimeout(expiry.current);};
  },[]);
  useEffect(() => {
    epoch.current++; flight.current?.abort(); flight.current=null; clearTimeout(expiry.current); setStatus(null); setError(''); setBusy(false);
    if (live && !paused) load();
    const timer = live && !paused ? setInterval(load,2000) : null;
    return () => clearInterval(timer);
  },[live,revision,load,paused]);
  return <div className="space-y-3 text-sm" data-testid="real-observer-status">
    <p>Gerçek hesap · yalnız gözlem. Aşağıdaki gerçek hesap verisi demo işlem fiyatlarından ayrıdır; motorun veya emirlerin veri kaynağını değiştirmez.</p>
    <label className="flex gap-2"><input aria-label="Gözlemci canlı yenileme" type="checkbox" checked={live} onChange={e=>setLive(e.target.checked)}/>Canlı yenileme · 2 saniyede bir · model ücreti yok</label>
    <Button variant="outline" disabled={busy||paused} onClick={load}>Gözlemci durumunu oku</Button>
    <p className="text-xs text-muted-foreground">Aynı salt okunur kaynak Broker ve Dashboard’da kullanılır. Alım yaşı 5 saniyeyi geçerse tutarlar/fiyatlar gizlenir; bu yeni piyasa tick’i veya 24/7 çalışma garantisi değildir.</p>
    {error&&<p role="alert" className="text-destructive">{error}</p>}
    {status&&!paused&&<div className="space-y-2 rounded border border-border p-3" role="status">
      <p>Eşleme: {status.paired?'var':'yok'} · Bağlantı: {status.connected?'güncel':'yok / eski'} · Salt okunur: evet · İşlem izni: kapalı · Trade hazır: hayır</p>
      <p>Hesap: {status.login_masked} · Sunucu: {status.server}</p>
      <p className="text-xs">Kaynak: cloud_mt5_real_observer · Alınma: {status.received_at||'—'}</p>
      {status.connected?<><dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">{[['balance','Bakiye'],['equity','Özkaynak'],['profit','Açık kâr / zarar'],['margin','Marjin'],['margin_free','Serbest marjin']].map(([key,label])=><div key={key}><dt>{label}</dt><dd className="font-mono">{money(status.account?.[key],status.account?.currency)}</dd></div>)}</dl>
        <table className="w-full text-left text-xs"><thead><tr><th>Enstrüman</th><th>Bid</th><th>Ask</th></tr></thead><tbody>{Object.entries(status.quotes).map(([symbol,q])=><tr key={symbol}><td>{symbol}</td><td>{q.bid}</td><td>{q.ask}</td></tr>)}</tbody></table></>:<p>Güncel veri doğrulanmadığı için hesap tutarları ve fiyatlar gizlendi.</p>}
    </div>}
  </div>;
}

export default function RealObserverStatus({embedded = false,...props}) {
  const {user} = useAuth();
  if (user?.role !== 'owner') return null;
  return embedded ? <OwnerObserverStatus {...props}/> : <Panel title="Cloud MT5 · gerçek hesap gözlemcisi" right={<Tag>Yalnız veri · emir kapalı</Tag>}><OwnerObserverStatus {...props}/></Panel>;
}
